import React, { useEffect, useState } from "react";
import { useI18n } from "../../i18n";
import { formatAmount } from "../utils/money";

const PERCENTAGE_CHIPS = [0, 5, 10, 15, 20];

// A phone's decimal keypad usually offers "," rather than "." — "1,5" means 1.5.
const parseAmount = (text) => {
  const value = Number(String(text).replace(",", "."));
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/**
 * Order level discount. Percentages cover the everyday "regular customer"
 * gesture; the fixed field covers rounding the total down to a round number.
 */
const DiscountRow = ({ discount, discountAmount, currency, onChange }) => {
  const { t } = useI18n();
  const fixedValue = discount.type === "fixed" ? Number(discount.value) || 0 : 0;

  // What the cashier typed, kept as text so "1," survives until the next digit.
  // Reset when the discount changes from elsewhere (a chip, a held ticket).
  const [draft, setDraft] = useState(fixedValue ? String(fixedValue) : "");
  useEffect(() => {
    setDraft((current) =>
      parseAmount(current) === fixedValue
        ? current
        : fixedValue
          ? String(fixedValue)
          : "",
    );
  }, [fixedValue]);

  return (
    <div className="px-4 py-3 border-t border-slate-800">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {t("pos.ticket.discount")}
        </span>
        {discountAmount > 0 && (
          <span className="text-xs font-bold text-amber-400 tabular-nums">
            −{formatAmount(discountAmount)} {currency}
          </span>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {PERCENTAGE_CHIPS.map((percentage) => {
          const isActive =
            discount.type === "percentage" &&
            Number(discount.value) === percentage;

          return (
            <button
              key={percentage}
              type="button"
              onClick={() => onChange({ type: "percentage", value: percentage })}
              className={`shrink-0 h-10 px-3.5 rounded-xl text-sm font-bold border transition-all active:scale-95 ${
                isActive
                  ? "bg-amber-500 border-amber-400 text-slate-900"
                  : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {percentage === 0 ? t("pos.ticket.none") : `${percentage}%`}
            </button>
          );
        })}

        <div className="shrink-0 flex items-center h-10 rounded-xl bg-slate-800 border border-slate-700 overflow-hidden">
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0"
            dir="ltr"
            value={draft}
            onChange={(event) => {
              const text = event.target.value.replace(/[^0-9.,]/g, "");
              if (!/^\d*([.,]\d{0,3})?$/.test(text)) return;
              setDraft(text);
              onChange({ type: "fixed", value: parseAmount(text) });
            }}
            aria-label={t("pos.ticket.discount")}
            className="w-16 h-full bg-transparent px-3 text-sm font-bold text-white placeholder-slate-500 focus:outline-none tabular-nums"
          />
          <span className="pe-3 text-xs text-slate-400">{currency}</span>
        </div>
      </div>
    </div>
  );
};

export default DiscountRow;
